import { useCallback, useMemo } from "react"
import { defineMessages, useIntl } from "react-intl"
import { MAX_INPUT_LENGTH } from "../constants"

export const message = defineMessages({
    required: {
        id: "validator.required",
        defaultMessage: "this is required",
    },
    email: {
        id: "validator.invalid.email",
        defaultMessage: "invalid email format",
    },
    phone: {
        id: "validator.invalid.phone",
        defaultMessage: "invalid phone format",
    },
    filename: {
        id: "validator.invalid.filename",
        defaultMessage: "name can't contain '/'",
    },
    max: {
        id: "validator.invalid.max",
        defaultMessage: "should not more than {num} characters",
    },
    min: {
        id: "validator.invalid.min",
        defaultMessage: "should not less than {num} characters",
    },
})

export default function useValidator() {
    const intl = useIntl()

    const noAllSpace = useCallback(
        (str: string | number) => {
            if (typeof str === "string")
                return !!str.trim() || intl.formatMessage(message.required)
            return true
        },
        [intl],
    )
    const noSlashInFilename = useCallback(
        (str: string) =>
            !str.includes("/") || intl.formatMessage(message.filename),
        [intl],
    )

    const applyValidations = useCallback(
        (str: string, rules: Array<(str: string) => Boolean | string>) => {
            for (let i = 0; i < rules.length; i++) {
                const result = rules[i](str)
                if (typeof result === "string") {
                    return result
                }
            }
            return true
        },
        [],
    )

    const required = useMemo(
        () => ({
            required: intl.formatMessage(message.required),
            validate: noAllSpace,
        }),
        [intl, noAllSpace],
    )

    const email = useMemo(
        () => ({
            ...required,
            pattern: {
                value: /^[-a-z0-9~!$%^&*_=+}{'?]+(\.[-a-z0-9~!$%^&*_=+}{'?]+)*@([a-z0-9_][-a-z0-9_]*(\.[-a-z0-9_]+)*\.(aero|arpa|biz|com|coop|edu|gov|info|int|mil|museum|name|net|org|pro|travel|mobi|[a-z][a-z])|([0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}))(:[0-9]{1,5})?$/i,
                message: intl.formatMessage(message.email),
            },
            maxLength: {
                value: MAX_INPUT_LENGTH,
                message: intl.formatMessage(message.max, {
                    num: MAX_INPUT_LENGTH,
                }),
            },
        }),
        [intl, required],
    )

    const emailNotRequired = useMemo(
        () => ({
            pattern: {
                value: /^[-a-z0-9~!$%^&*_=+}{'?]+(\.[-a-z0-9~!$%^&*_=+}{'?]+)*@([a-z0-9_][-a-z0-9_]*(\.[-a-z0-9_]+)*\.(aero|arpa|biz|com|coop|edu|gov|info|int|mil|museum|name|net|org|pro|travel|mobi|[a-z][a-z])|([0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}))(:[0-9]{1,5})?$/i,
                message: intl.formatMessage(message.email),
            },
        }),
        [intl],
    )

    const password = useMemo(
        () => ({
            ...required,
            minLength: {
                value: 6,
                message: intl.formatMessage(message.min, { num: 6 }),
            },
            maxLength: {
                value: MAX_INPUT_LENGTH,
                message: intl.formatMessage(message.max, {
                    num: MAX_INPUT_LENGTH,
                }),
            },
        }),
        [intl, required],
    )
    const maxInputLength = useMemo(
        () => ({
            ...required,
            maxLength: {
                value: MAX_INPUT_LENGTH,
                message: intl.formatMessage(message.max, {
                    num: MAX_INPUT_LENGTH,
                }),
            },
        }),
        [intl, required],
    )
    const phone = useMemo(
        () => ({
            ...required,
            maxLength: {
                value: 20,
                message: intl.formatMessage(message.max, { num: 20 }),
            },
            pattern: {
                value: /^\+?([0-9][- ]?)+[0-9]$/,
                message: intl.formatMessage(message.phone),
            },
        }),
        [intl, required],
    )
    const filename = useMemo(
        () => ({
            ...required,
            maxLength: {
                value: MAX_INPUT_LENGTH,
                message: intl.formatMessage(message.max, {
                    num: MAX_INPUT_LENGTH,
                }),
            },
            validate: (str: string) =>
                applyValidations(str, [noAllSpace, noSlashInFilename]),
        }),
        [intl, required, applyValidations, noAllSpace, noSlashInFilename],
    )

    return {
        email,
        filename,
        required,
        password,
        phone,
        emailNotRequired,
        maxInputLength,
    }
}
